<template>
  <div
    class="relative h-svh pt-inset-top"
    :class="[
      hasImage ? 'text-white' : 'text-light-950 dark:text-dark-50'
    ]"
  >
    <div class="relative mx-auto h-full overflow-hidden" :class="[ hasImage ? 'flex items-center justify-center' : '']">
      <IconBase name="cute/spinner" :width="20" class="spin text-white/50 relative z-[1]" v-if="hasImage" />

      <img :src="post.medias[0]?.getSize('m')" class="absolute top-0 left-0 w-full h-full z-[2] object-contain object-top" :style="{ transform: `translateZ(1px)` }" draggable="false" v-if="hasImage" />

      <div
        class="absolute top-0 left-0 h-16 w-full bg-gradient-to-b from-charcoal-950/60 z-[5] to-charcoal-950/0"
        v-if="hasImage"
      />

      <div
        class="absolute bottom-0 left-0 h-12 w-full bg-gradient-to-t from-charcoal-950/90 z-[5] to-charcoal-950/0"
        v-if="hasImage"
      />

      <div class="absolute bottom-0 z-[9] left-0 right-0 top-0 flex items-center justify-center" @touchstart="onTap">
        <div class="relative">
          <IconBase name="cute/heart-fill" class="text-flamingo-600" :class="[ hasLiked && !isAlreadyLiked ? 'animate-heart-2' : 'opacity-0' ]" :width="46" />
        </div>
      </div>

      <div class="z-10 text-center space-y-4 absolute bottom-0 left-0 right-0 top-0 flex flex-col bg-dark-950/60 backdrop-blur-lg items-center justify-center" v-if="isSpicy">
        <div class="space-y-1">
          <ImageEmoji name="Fire" class="size-10 z-10 mx-auto" />
          <p class="font-bold text-md">{{ $t('components.Story.spicy.title') }}</p>
        </div>
        <ButtonBase color="spicy" @click="() => isSpicyShow = true">
          {{ $t('components.Story.spicy.show') }}
        </ButtonBase>
        <ButtonLink size="sm" @click="() => userStore.setSpicy()">{{ $t('components.Story.spicy.unlock') }}</ButtonLink>
      </div>

      <div class="relative max-w-md mx-auto bg-light-200 dark:bg-dark-800 rounded-3xl overflow-hidden" v-if="!hasImage">
        <div class="pb-inset-top mt-20 overflow-auto" :style="{ height: 'calc(100vh - var(--inset-top) - 8rem - var(--inset-bottom))' }">
          <div class="p-6">
            <NuxtLink :to="{ query: { storyCreate: 'true', question: post.attachments.question } }" class="text-base bg-light-300 dark:bg-dark-900 rounded-full py-2.5 px-3 leading-tight font-bold mb-3 text-shadow-none shadow-sm -mx-2 inline-flex items-center space-x-2" v-if="post.attachments?.question">
              <IconBase name="cute/question-circle-fill" :width="16" />
              <div class="grow pr-4">
                <p class="text-2xs font-bold -mb-0.5 opacity-50 leading-tight">{{ $t('components.Story.interactive.question.title')}}</p>
                <p class="leading-tight font-medium">{{ post.attachments.question }}</p>
              </div>

              <IconBase name="cute/angle-right" :width="16" />
            </NuxtLink>

            <InputTextareaPaper
              v-model="post.content"
              body-class="text-post text-post-xl"
              is-read-only
            />
          </div>
        </div>

        <div class="absolute bottom-0 left-0 right-0 z-20 pointer-events-none h-16 bg-gradient-to-t from-light-200/50 to-light-200/0 dark:from-dark-800/50 dark:to-dark-800/0" />
      </div>

      <div class="h-full w-10 absolute bottom-0 left-0 z-10" :style="{ transform: `translateZ(2px)` }" @click.stop.prevent="() => emit('prev')" />
      <div class="h-full w-10 absolute bottom-0 right-0 z-10" :style="{ transform: `translateZ(2px)` }" @click.stop.prevent="() => emit('next')" />
    </div>

    <div
      class="absolute top-inset-top right-0 left-0 font-medium z-20 py-3 pr-3"
      :class="[
        hasImage ? 'bg-gradient-to-b from-charcoal-950/50 to-charcoal-950/0' : ''
      ]"
    >
      <div class="flex items-center pb-8 max-w-md mx-auto">
        <NuxtLink :to="{ query: { ...(route?.query ?? {}), user: user.username }}" class="grow flex items-center space-x-2.5 pl-4">
          <UserPicture :user="usersStore.getItem(post.owner)" size="base" />

          <div class="grow" :class="[ hasImage ? 'text-shadow' : '' ]">
            <p class="text-md line-clamp-1 !leading-tight break-all">
              {{ user.name }}

              <span class="text-xs">{{ dayjs(post.createdAt).fromNow() }}</span>
            </p>

            <div class="flex items-center">
              <span class="shrink-0 text-xs text-white py-0.5 mt-0.5 align-middle pl-1.5 pr-2 inline-flex items-center text-shadow-none rounded mr-2" :class="[ post.visibility === 'friends' ? 'bg-cobra-500' : 'bg-tiger-500' ]" v-if="post.visibility !== 'public'">
                <IconBase :name="post.visibility === 'friends' ? 'cute/star-fill' : 'cute/check-fill'" class="mr-0.5" :width="12" />
                <span class="grow">{{ $t(`components.Post.visibility.${post.visibility}.titleShort`) }}</span>
              </span>

              <span class="line-clamp-1 -mt-0.5 text-xs !leading-tight break-all" :class="[ mutuals.includes(post.owner) ? 'text-urchin-300' : '' ]" v-else>
                {{ mutuals.includes(post.owner) ? $t('components.User.follow.mutual') : following.includes(post.owner) ? $t('components.User.follow.following', { name: user.name }) : (followers.includes(post.owner) ? $t('components.User.follow.follower') : '') }}
              </span>
            </div>
          </div>
        </NuxtLink>

        <ImageEmoji name="Fire" class="size-8" size="sm" v-if="post.isSpicy && !isSpicy" />

        <PostActions :post="post" :types="[ 'menu' ]" size="md" v-if="!isInputFocused && messageRaw.length <= 0" />

        <div class="size-12 flex items-center justify-center" @click.stop.prevent="onClose">
          <IconBase name="cute/cross" :width="24" />
        </div>
      </div>
    </div>

    <div
      class="absolute bottom-0 left-0 font-medium w-full z-20 pt-16"
      :style="{
        transform: `translateZ(2px)`,
        bottom: (kHeight ?? 0) + 'px'
      }"
      :class="[
        (kHeight ?? 0) > 0 ? '' : 'pb-inset-bottom',
        hasImage ? 'bg-gradient-to-t from-charcoal-950/90 via-charcoal-950/60 to-charcoal-950/0' : ''
      ]"
    >
      <div
        class="max-w-md mx-auto text-md space-y-4 pb-4"
      >
        <div class="space-y-1 text-shadow p-4 overflow-auto max-h-40 -mb-4" v-if="hasImage">
          <NuxtLink :to="{ query: { storyCreate: 'true', question: post.attachments.question } }" class="text-base text-dark-50 bg-dark-900 rounded-full py-2.5 px-3 leading-tight font-bold mb-3 text-shadow-none shadow-md -mx-2 inline-flex items-center space-x-2" v-if="post.attachments?.question">
            <IconBase name="cute/question-circle-fill" :width="16" />
            <div class="grow pr-4">
              <p class="text-2xs font-bold -mb-0.5 opacity-50 leading-tight">{{ $t('components.Story.interactive.question.title')}}</p>
              <p class="leading-tight font-medium">{{ post.attachments.question }}</p>
            </div>

            <IconBase name="cute/angle-right" :width="16" />
          </NuxtLink>

          <p class="leading-normal text-md" v-if="stripTags(post.content).length > 0">{{ stripTags(post.content) }}</p>
        </div>

        <div class="space-y-2 relative z-20 bg-white dark:bg-dark-800 p-4 m-3 rounded-2xl" v-if="(isInputFocused && !currentUser?.isSubscriber && !conversation)">
          <p class="text-base leading-snug font-bold mb-4">
            {{ $t('components.User.requests.remaining', currentUser?.requests ?? 0) }}
          </p>

          <div class="space-y-1.5">
            <ButtonBase :to="{ query: { subscribe: 'true' }}" size="sm" is-full>
              {{ $t('components.User.requests.unlock') }}
            </ButtonBase>
          </div>
        </div>

        <div class="flex items-center space-x-2 px-4 min-h-10">
          <PostActions size="md" @like="() => sentimentsStore.skip({ postId: post._id })" :post="post" :types="[ 'likes', 'comments' ]" v-if="!isInputFocused && messageRaw.length <= 0" />

          <!-- <div class="flex items-center space-x-2 grow rounded-3xl overflow-hidden bg-charcoal-100 dark:bg-dark-600" v-if="((conversation && conversation.isActive) || !conversation) && post.owner !== currentUser?._id">
            <div class="overflow-auto grow max-h-36">
              <InputTextareaPaper
                v-model="message"
                v-model:raw="messageRaw"
                :placeholder="$t('components.User.message.placeholder')"
                class="!bg-transparent px-3.5 py-2"
                @focus="() => isInputFocused = true"
                @blur="() => isInputFocused = false"
              />
            </div>
          </div> -->

          <div class="grow" />

          <UserFollowButton :user="user" size="xs" is-text v-if="!following.includes(post.owner) && currentUser?._id !== post.owner" />

          <!-- <ButtonBase size="xs" color="slate" :is-loading="isLoading" :is-disabled="messageRaw.length <= 0" style-type="round" class="shrink-0" v-if="messageRaw.length > 0 || isInputFocused" >
            <IconBase name="cute/send-fill" :width="22" />
          </ButtonBase> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import { Post, User } from '@/classes'
import UsersStore from '@/store/users'
import UserStore from '@/store/user'
import SentimentsStore from '@/store/sentiments'
import ReactionsStore from '@/store/reactions'
import ConversationsStore from '@/store/conversations'
import { notificationsRemove } from '~/utils/native/usePushNotifications'
import { getKeyboard } from '~/utils/native/useKeyboard'
import { ImpactStyle } from '@capacitor/haptics'

const props = withDefaults(defineProps<{
  post: Post,
  isActive: boolean
  isCurrent: boolean
}>(), {
  isActive: false,
  isCurrent: false
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'next'): void
  (e: 'prev'): void
}>()

const { kHeight } = getKeyboard()
const route = useRoute()

const { post, isCurrent } = toRefs(props)

const isSpicyShow = ref(false)
const isSpicy = computed(() => post.value.isSpicy && !isSpicyShow.value && (isSpicyUntil.value ? isSpicyUntil.value <= new Date() : true))

const sentimentsStore = SentimentsStore()
const conversationStore = ConversationsStore()
const reactionsStore = ReactionsStore()
const usersStore = UsersStore()
const userStore = UserStore()
const { currentUser, isSpicyUntil, mutuals, following, followers } = storeToRefs(userStore)

const hasImage = computed(() => post.value.medias?.[0]?.medias?.[0]?.src)

const user = computed(() => usersStore.getItem(props.post.owner) as User)

const isInputFocused = ref(false)
const messageRaw = ref('')

const conversationId = ref('')
const conversation = computed(() => conversationStore.getItem(conversationId.value))

watch(isCurrent, async (v) => {
  if (v && currentUser.value) {
    await Promise.all([
      (async () => {
        if (!currentUser.value) return

        const conversation = await conversationStore.users([
          post.value.owner,
          currentUser.value._id
        ])
        
        conversationId.value = conversation?._id ?? ''
      })(),
      (() => {
        sentimentsStore.skip({ postId: post.value._id })
        notificationsRemove(post.value._id)
      })()
    ])
  }
}, { immediate: true })

const isLoading = ref(false)

const hasLiked = computed(() => (post.value.reactions.some(reaction => reaction.value === 'like' && reaction.owner === currentUser.value?._id) && isTempLike.value !== false) || isTempLike.value === true)
const isTempLike: Ref<boolean | null> = ref(null)
const isAlreadyLiked = ref(false)

const { onTap } = doubleTap(() => onLike())

const onLike = async () => {
  if (!post.value || isLoading.value || !currentUser.value) return

  isAlreadyLiked.value = false
  isLoading.value = true

  if (hasLiked.value) {
    isTempLike.value = false

    await reactionsStore.remove({
      value: 'like',
      type: 'post',
      id: post.value._id,
      root: post.value._id
    })
  } else {
    vibrate(ImpactStyle.Light)    
    isTempLike.value = true

    useNuxtApp().$amplitude.logEvent(post.value.parent ? 'Comment_React' : (post.value.type === 'story' ? 'Story_React' : 'Post_React')), {
      type: 'like',
      source: route.fullPath
    }

    await reactionsStore.create({
      value: 'like',
      type: 'post',
      id: post.value._id,
      root: post.value._id
    })
  }

  isTempLike.value = null
  isLoading.value = false
}

const onClose = () => {
  emit('close')
}

onMounted(() => {
  if (hasLiked.value) isAlreadyLiked.value = true
})

</script>

<style scoped>
.animate-heart {
  animation: heart 0.3s cubic-bezier(0.43, 0.22, 0.32, 1.44) forwards;
}

@keyframes heart {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.35) rotate(2deg);
  }
  100% {
    transform: scale(1);
  }
}

.animate-heart-2 {
  animation: heart-2 0.8s ease-out forwards;
  animation-delay: 0s;
}

@keyframes heart-2 {
  0% {
    transform: scale(0.2);
    opacity: 1;
  }

  30% {
    transform: scale(1.1);
    opacity: 1;
  }

  50% {
    transform: scale(1);
    opacity: 1;
  }

  100% {
    transform: scale(3);
    opacity: 0;
  }
}
</style>